// Buttons as Apple draws them, in TUNE's colours: filled (emerald, for the one main action), tinted, gray, plain, and
// red for what can't be undone. A busy button keeps its place and focus, shows a spinner, and ignores presses.
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react';
import { Link, type LinkProps } from 'react-router';
import s from './Button.module.css';
import { cx } from './cx';
import { Spinner } from './Status';

export type ButtonVariant = 'filled' | 'tinted' | 'gray' | 'plain' | 'destructive' | 'destructivePlain';
export type ButtonSize = 'small' | 'regular' | 'large';

interface Look {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** As wide as what it's in. */
  wide?: boolean;
  /** An icon before the label. */
  icon?: ReactNode;
}

function classes({ variant = 'filled', size = 'regular', wide }: Look, busy: boolean, extra?: string): string {
  return cx(s.button, s[variant], size !== 'regular' && s[size], wide && s.wide, busy && s.busy, extra);
}

function Inside({ icon, busy, children }: { icon?: ReactNode; busy: boolean; children: ReactNode }) {
  return (
    <>
      {icon && (
        <span className={s.icon} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={s.label}>{children}</span>
      {busy && (
        <span className={s.spinner}>
          <Spinner size="small" label={null} />
        </span>
      )}
    </>
  );
}

export function Button({
  variant,
  size,
  wide,
  icon,
  busy = false,
  className,
  type = 'button',
  onClick,
  children,
  ref,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & Look & { busy?: boolean; ref?: Ref<HTMLButtonElement> }) {
  return (
    <button
      ref={ref}
      type={type}
      className={classes({ variant, size, wide }, busy, className)}
      aria-disabled={busy || undefined}
      aria-busy={busy || undefined}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        if (busy) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      {...rest}
    >
      <Inside icon={icon} busy={busy}>
        {children}
      </Inside>
    </button>
  );
}

/** A link that looks like a button: for going somewhere, where a button does something. */
export function ButtonLink({ variant, size, wide, icon, className, children, ...rest }: LinkProps & Look) {
  return (
    <Link className={classes({ variant, size, wide }, false, className)} {...rest}>
      <Inside icon={icon} busy={false}>
        {children}
      </Inside>
    </Link>
  );
}

/** A link to a file or another site, looking like a button. */
export function ButtonAnchor({
  variant,
  size,
  wide,
  icon,
  className,
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & Look) {
  return (
    <a className={classes({ variant, size, wide }, false, className)} {...rest}>
      <Inside icon={icon} busy={false}>
        {children}
      </Inside>
    </a>
  );
}

/** A round button with only an icon. It's always named: the name is read, and shown in a tip on hover. */
export function IconButton({
  label,
  icon,
  variant = 'quiet',
  size = 'regular',
  className,
  type = 'button',
  ref,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  label: string;
  icon: ReactNode;
  variant?: 'plain' | 'gray' | 'quiet';
  size?: 'small' | 'regular';
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      data-tip={label}
      className={cx(
        s.iconButton,
        variant === 'plain' && s.plainIcon,
        variant === 'gray' && s.grayIcon,
        variant === 'quiet' && s.quietIcon,
        size === 'small' && s.smallIcon,
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true" className={s.icon}>
        {icon}
      </span>
    </button>
  );
}
